/** @jsx jsx */
import React, { useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { Marker } from './meta/Marker';
import { GlobalStyles, Paragraph, Spinner, TypographyVisualStyle } from 'react-magma-dom';
import { Main } from './a11y/landmark/Main';
import { AlfredGlobalStyles } from './basic/AlfredGlobalStyles';
import { ContentContainer, WidgetHeader, WidgetContent, WidgetContentInner, SignInFooter, Link } from './styled';
import { checkUserSession, revokeAppTokens } from '../api';
import { CengageLogo } from "./basic/CengageLogo";

export const TEST_ID = {
  MARKER: 'marker-logout-progress',
};

export interface LogoutProgressProps {
  onUserSessionOk: () => void,
  onNoUserSessionFound: () => void,
  onUserSessionFailed: () => void,
  onAllLogoutsComplete: () => void,
}

export const LogoutProgress: React.FunctionComponent<LogoutProgressProps> = (props: LogoutProgressProps) => 
{
  const { onUserSessionOk, onNoUserSessionFound, onUserSessionFailed, onAllLogoutsComplete } = props;
  const appLogoutMap = window.backend.appLogoutUrlMap;
  const checkSession = window.backend.checkSession;
  const orgUrl = window.backend.orgUrl;
  
  useEffect(() => {
    if (checkSession) {
      checkUserSession(orgUrl).then((response) => {
        if (response.ok) {
          onUserSessionOk();
        } else {
          onNoUserSessionFound();
        }
      }).catch(() => 
      {
        onUserSessionFailed();
      });
    } else {
      Promise.allSettled(Object.keys(appLogoutMap).map((appLogoutName: string) => {
        return revokeAppTokens(appLogoutMap[appLogoutName]);
      })).then(() => {
        onAllLogoutsComplete();
      });
    }
  }, [appLogoutMap, checkSession, orgUrl, onUserSessionOk, onNoUserSessionFound, onUserSessionFailed, onAllLogoutsComplete]);

  return (
    <React.Fragment>
      <GlobalStyles />
      <AlfredGlobalStyles />
      <ContentContainer>
        <WidgetHeader>
          <CengageLogo />
        </WidgetHeader>
        <Main>
          <WidgetContent>
            <WidgetContentInner>
              <div
                css={css`
                  // Center this and make it not terribly wide
                  margin: 0 auto;
                  max-width: 60vmax;
                `}
              >
                <Marker testId={TEST_ID.MARKER} />
                <Spinner aria-label="Checking Mergeability" 
                  css={css`
                  display: block;
                  margin: 10px auto;
                  `}
                  size={40} />
                <Paragraph
                  css={css`
                  text-align: center;
                `}
                  visualStyle={TypographyVisualStyle.bodyMedium}
                >
                Logging you out. Please wait.
                </Paragraph>
              </div>
            </WidgetContentInner>
            <SignInFooter>
              Return to {' '}
              <Link href="https://www.cengage.com">Cengage</Link>
            </SignInFooter>
          </WidgetContent>
        </Main>
      </ContentContainer>
    </React.Fragment>
  );
};
